import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  LayoutDashboard, Building2, CalendarDays, BookOpen,
  Share2, BarChart3, Settings, LogOut, Home, Shield, Menu, X, MessageSquare, CreditCard, Globe, HelpCircle, Users, Star, Brain, TrendingUp
} from 'lucide-react';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/properties', icon: Building2, label: 'Properties' },
  { to: '/bookings', icon: BookOpen, label: 'Bookings' },
  { to: '/calendar', icon: CalendarDays, label: 'Calendar' },
  { to: '/ai-pricing', icon: Brain, label: 'AI Pricing' },
  { to: '/demand-forecast', icon: TrendingUp, label: 'Forecasting' },
  { to: '/messaging', icon: MessageSquare, label: 'Messaging' },
  { to: '/reviews', icon: Star, label: 'Reviews' },
  { to: '/integrations', icon: Globe, label: 'Integrations' },
  { to: '/reports', icon: BarChart3, label: 'Reports' },
  { to: '/team', icon: Users, label: 'Team Members' },
  { to: '/subscription', icon: CreditCard, label: 'Subscription' },
  { to: '/settings', icon: Settings, label: 'Settings' },
  { to: '/docs', icon: HelpCircle, label: 'Help & Docs' },
];

const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const closeMobile = () => setMobileOpen(false);

  const sidebarContent = (
    <>
      <div className="px-6 py-5 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Home className="w-7 h-7 text-airbnb-pink" />
            <span className="text-xl font-bold text-airbnb-gray tracking-tight">iHostPMS</span>
          </div>
          <button onClick={closeMobile} className="lg:hidden p-1 rounded-lg hover:bg-gray-100" data-testid="close-mobile-menu">
            <X className="w-5 h-5 text-airbnb-gray" />
          </button>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={closeMobile}
            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                isActive
                  ? 'bg-airbnb-light-pink text-airbnb-pink'
                  : 'text-airbnb-medium-gray hover:bg-gray-50 hover:text-airbnb-gray'
              }`
            }
          >
            <item.icon className="w-5 h-5" />
            <span className="flex-1">{item.label}</span>
            {item.badge && (
              <span className="px-1.5 py-0.5 text-[10px] font-bold bg-purple-100 text-purple-700 rounded-full">
                {item.badge}
              </span>
            )}
          </NavLink>
        ))}

        {user?.role === 'super_admin' && (
          <>
            <div className="border-t border-gray-100 my-3" />
            <NavLink
              to="/platform"
              onClick={closeMobile}
              data-testid="nav-platform-admin"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                  isActive
                    ? 'bg-gray-800 text-white'
                    : 'text-airbnb-medium-gray hover:bg-gray-100 hover:text-airbnb-gray'
                }`
              }
            >
              <Shield className="w-5 h-5" />
              <span>Platform Admin</span>
            </NavLink>
          </>
        )}
      </nav>

      <div className="p-4 border-t border-gray-100">
        <div className="flex items-center gap-3 px-2 mb-3">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-airbnb-light-pink flex items-center justify-center text-airbnb-pink font-bold text-sm">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-airbnb-gray truncate">{user?.name || 'User'}</p>
            <p className="text-xs text-airbnb-medium-gray truncate">{user?.email}</p>
          </div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={handleLogout}
          className="flex items-center gap-2 px-3 py-2 w-full rounded-xl text-sm text-airbnb-medium-gray hover:bg-red-50 hover:text-red-600 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          <span>Sign out</span>
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-xl shadow-md border border-gray-100"
        data-testid="mobile-menu-btn"
      >
        <Menu className="w-5 h-5 text-airbnb-gray" />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/30 z-40" onClick={closeMobile} />
      )}

      {/* Mobile sidebar */}
      <aside
        className={`lg:hidden fixed left-0 top-0 bottom-0 w-72 bg-white z-50 flex flex-col transform transition-transform duration-300 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="mobile-sidebar"
      >
        {sidebarContent}
      </aside>

      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-white border-r border-gray-100 z-40 flex-col" data-testid="sidebar">
        {sidebarContent}
      </aside>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 flex items-center justify-around py-2 px-1" style={{ zIndex: 10000 }} data-testid="bottom-nav">
        {navItems.slice(0, 5).map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg text-[10px] font-medium ${
                isActive ? 'text-airbnb-pink' : 'text-airbnb-medium-gray'
              }`
            }
          >
            <item.icon className="w-5 h-5" />
            <span>{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </>
  );
};

export default Sidebar;
